import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ShoppingCart, Loader2, ArrowLeft, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { CartItem } from '@/components/cart/cart-item'
import {
  useCartStore,
  useCartItems,
  calculateCartTotals,
} from '@/stores/cart-store'
import { supabase } from '@/lib/supabase'
import type { Product } from '@/types/database'
import {
  callEdgeFunction,
  type EdgeFunctionErrorCode,
} from '@/lib/edge-functions'
import { toast } from 'sonner'

function getCheckoutErrorMessage(
  errorCode: EdgeFunctionErrorCode | null,
  fallbackError: string | null
): string {
  if (errorCode === 'network_error') {
    return 'Network issue while starting checkout. Please try again.'
  }

  if (errorCode === 'session_expired') {
    return 'Your session expired and checkout could not be started. Please sign in again.'
  }

  if (errorCode === 'unauthorized') {
    return 'Checkout authorization failed. Please refresh and try again.'
  }

  return fallbackError || 'Failed to start checkout. Please try again.'
}

export function CartPage() {
  const cartItems = useCartItems()
  const clearCart = useCartStore(state => state.clearCart)

  const [products, setProducts] = useState<Map<string, Product>>(new Map())
  const [isLoading, setIsLoading] = useState(false)
  const [isCheckingOut, setIsCheckingOut] = useState(false)

  // Fetch product details when cart items change
  useEffect(() => {
    if (cartItems.length === 0) {
      setProducts(new Map())
      return
    }

    const productIds = cartItems.map(item => item.productId)

    setIsLoading(true)

    supabase
      .from('products')
      .select('*')
      .in('id', productIds)
      .in('status', ['featured', 'archived'])
      .then(({ data, error }) => {
        if (error) {
          console.error('Failed to fetch products:', error)
          toast.error('Failed to load cart items')
        } else if (data) {
          setProducts(new Map(data.map(p => [p.id, p as Product])))
        }
        setIsLoading(false)
      })
  }, [cartItems])

  // Calculate totals
  const { subtotal, originalTotal, totalSavings, itemCount } = calculateCartTotals(cartItems, products)

  // Handle checkout
  const handleCheckout = async () => {
    if (cartItems.length === 0) return

    setIsCheckingOut(true)

    try {
      const { data: { session } } = await supabase.auth.getSession()
      const result = await callEdgeFunction<{ url: string }>('create-checkout-session', {
        method: 'POST',
        withAuth: true,
        body: {
          origin: window.location.origin,
          customerEmailHint: session?.user?.email,
          items: cartItems.map(item => ({
            productId: item.productId,
            variantId: item.variantId,
            quantity: item.quantity,
            customInputs: item.customInputs,
          })),
        },
      })

      if (result.error || !result.data?.url) {
        console.error('Checkout error:', {
          error: result.error,
          errorCode: result.errorCode,
          status: result.status,
        })
        toast.error(getCheckoutErrorMessage(result.errorCode, result.error))
        return
      }

      // Redirect to Stripe Checkout
      window.location.href = result.data.url
    } catch (err) {
      console.error('Checkout error:', err)
      toast.error('Failed to start checkout. Please try again.')
    } finally {
      setIsCheckingOut(false)
    }
  }

  /** Composite key for rendering unique cart items */
  const itemKey = (item: { productId: string; variantId: string | null }) =>
    item.variantId ? `${item.productId}::${item.variantId}` : item.productId

  if (isLoading) {
    return (
      <div className="container mx-auto flex min-h-[50vh] items-center justify-center px-4 py-8">
        <Loader2 className="size-8 animate-spin text-zinc-500" />
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <Link
          to="/past-reviews"
          className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900"
        >
          <ArrowLeft className="size-4" />
          Continue Shopping
        </Link>
        <h1 className="mt-4 flex items-center gap-3 text-3xl font-bold">
          <ShoppingCart className="size-8" />
          Your Cart
          {itemCount > 0 && (
            <span className="rounded-full bg-red-500 px-3 py-1 text-sm font-bold">
              {itemCount} {itemCount === 1 ? 'item' : 'items'}
            </span>
          )}
        </h1>
      </div>

      {cartItems.length === 0 ? (
        /* Empty cart state */
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <ShoppingCart className="mb-4 size-20 text-zinc-700" />
          <h2 className="mb-2 text-2xl font-semibold text-zinc-400">Your cart is empty</h2>
          <p className="mb-6 text-zinc-600">
            Check out this week's deal or browse our past reviews!
          </p>
          <div className="flex gap-4">
            <Button asChild className="bg-red-600 hover:bg-red-700">
              <Link to="/">This Week's Deal</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/past-reviews">Browse Past Reviews</Link>
            </Button>
          </div>
        </div>
      ) : (
        /* Cart with items */
        <div className="grid gap-8 lg:grid-cols-3">
          {/* Cart items */}
          <div className="lg:col-span-2">
            <div className="rounded-lg border border-zinc-200 bg-white p-4 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="font-semibold text-zinc-900">Cart Items</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-zinc-500 hover:text-red-600"
                  onClick={clearCart}
                >
                  <Trash2 className="mr-1 size-4" />
                  Clear Cart
                </Button>
              </div>
              <Separator className="mb-4 bg-zinc-200" />
              <div className="divide-y divide-zinc-200">
                {cartItems.map(item => {
                  const product = products.get(item.productId)
                  if (!product) return null
                  return (
                    <CartItem
                      key={itemKey(item)}
                      item={item}
                      product={product}
                    />
                  )
                })}
              </div>
            </div>
          </div>

          {/* Order summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 rounded-lg border border-zinc-200 bg-white p-6 shadow-sm">
              <h2 className="mb-4 text-lg font-semibold text-zinc-900">Order Summary</h2>
              <Separator className="mb-4 bg-zinc-200" />

              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">Original Total</span>
                  <span className="text-zinc-400 line-through">
                    ${originalTotal.toFixed(2)}
                  </span>
                </div>
                {totalSavings > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-500">You Save</span>
                    <span className="font-medium text-green-600">
                      -${totalSavings.toFixed(2)}
                    </span>
                  </div>
                )}
                <Separator className="bg-zinc-200" />
                <div className="flex justify-between text-lg font-semibold">
                  <span className="text-zinc-900">Subtotal</span>
                  <span className="text-zinc-900">${subtotal.toFixed(2)}</span>
                </div>
                <p className="text-xs text-zinc-500">
                  Shipping and taxes calculated at checkout
                </p>
              </div>

              <Button
                className="mt-6 w-full bg-red-600 text-white hover:bg-red-700"
                size="lg"
                onClick={handleCheckout}
                disabled={isCheckingOut || cartItems.length === 0}
              >
                {isCheckingOut ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>Proceed to Checkout</>
                )}
              </Button>

              <div className="mt-4 flex items-center justify-center gap-2 text-xs text-zinc-400">
                <svg className="size-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C9.243 2 7 4.243 7 7v3H6c-1.103 0-2 .897-2 2v8c0 1.103.897 2 2 2h12c1.103 0 2-.897 2-2v-8c0-1.103-.897-2-2-2h-1V7c0-2.757-2.243-5-5-5zm0 2c1.654 0 3 1.346 3 3v3H9V7c0-1.654 1.346-3 3-3zm6 8v8H6v-8h12z" />
                </svg>
                Secure checkout powered by Stripe
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
